import { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Download, Loader2 } from 'lucide-react';
import { useAuthStore } from '../services/auth';
import PublicLayout from '../components/layout/PublicLayout';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://humanvu-api.mndev.co.za/api';

function WindowsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
    </svg>
  );
}

function LinuxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Body */}
      <path d="M12 2C9.5 2 8 4.5 8 7v3c0 1.5-.8 2.8-2 4-1 1-1.5 2.5-1 4 .5 1.5 2 2 3.5 2h7c1.5 0 3-.5 3.5-2 .5-1.5 0-3-1-4-1.2-1.2-2-2.5-2-4V7c0-2.5-1.5-5-4-5z" />
      {/* Eyes */}
      <circle cx="10" cy="7" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="14" cy="7" r="0.75" fill="currentColor" stroke="none" />
      {/* Beak */}
      <path d="M10.5 9h3L12 10.5 10.5 9z" fill="currentColor" stroke="none" />
      {/* Feet */}
      <path d="M9 20v1.5c0 .3-.5.5-1 .5H6.5" />
      <path d="M15 20v1.5c0 .3.5.5 1 .5h1.5" />
    </svg>
  );
}

function PlatformIcon({ platform, className }: { platform: string; className?: string }) {
  if (platform === 'Windows') return <WindowsIcon className={className} />;
  if (platform.includes('Linux')) return <LinuxIcon className={className} />;
  return <Download className={className} />;
}

interface VersionHistoryItem {
  version: string;
  notes: string;
  pub_date: string;
}

interface LatestVersionInfo {
  version: string;
  notes: string;
  pub_date: string;
  downloads: {
    windows: string;
    linux_appimage: string;
  };
  history: VersionHistoryItem[];
}

interface DownloadItem {
  platform: string;
  description: string;
  filename: string;
  url: string;
}

export default function Downloads() {
  const { isAuthenticated } = useAuthStore();
  const [availableDownloads, setAvailableDownloads] = useState<DownloadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [latestVersion, setLatestVersion] = useState<string>('');
  const [versionHistory, setVersionHistory] = useState<VersionHistoryItem[]>([]);

  useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/updates/latest`);
        if (response.ok) {
          const data: LatestVersionInfo = await response.json();
          setLatestVersion(data.version);
          setVersionHistory(data.history || []);

          // Build download items from the API response
          const downloads: DownloadItem[] = [];

          // Check Windows download
          try {
            const winResponse = await fetch(data.downloads.windows, { method: 'HEAD' });
            if (winResponse.ok) {
              const filename = data.downloads.windows.split('/').pop() || '';
              downloads.push({
                platform: 'Windows',
                description: 'For Windows 10/11 (64-bit)',
                filename,
                url: data.downloads.windows,
              });
            }
          } catch {
            // Windows download not available
          }

          // Check Linux AppImage download
          try {
            const linuxResponse = await fetch(data.downloads.linux_appimage, { method: 'HEAD' });
            if (linuxResponse.ok) {
              const filename = data.downloads.linux_appimage.split('/').pop() || '';
              downloads.push({
                platform: 'Linux (AppImage)',
                description: 'Universal Linux package',
                filename,
                url: data.downloads.linux_appimage,
              });
            }
          } catch {
            // Linux download not available
          }

          setAvailableDownloads(downloads);
        }
      } catch (error) {
        console.error('Failed to fetch latest version:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestVersion();
  }, []);

  const content = (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Downloads</h1>
        <p className="text-gray-500 dark:text-slate-400">
          Download the HumanVu client application for your platform
        </p>
      </div>

      {/* Version Info */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Latest Version</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">Version {latestVersion || '...'}</p>
          </div>
        </div>
      </Card>

      {/* Download Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 dark:text-primary-400" />
          <span className="ml-3 text-gray-500 dark:text-slate-400">Checking available downloads...</span>
        </div>
      ) : availableDownloads.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {availableDownloads.map((item) => (
            <Card key={item.platform} className="flex flex-col">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <PlatformIcon platform={item.platform} className="w-6 h-6 text-primary-500 dark:text-primary-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{item.platform}</h3>
                  <p className="text-sm text-gray-500 dark:text-slate-400">{item.description}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1 font-mono">{item.filename}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <a href={item.url} download>
                  <Button className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </a>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-slate-400">No downloads available yet.</p>
            <p className="text-sm text-gray-400 dark:text-slate-500 mt-2">
              Please contact your administrator for installation files.
            </p>
          </div>
        </Card>
      )}

      {/* Version History */}
      {versionHistory.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Version History</h2>
          <div className="space-y-4">
            {versionHistory.map((item, index) => (
              <div key={item.version} className={index > 0 ? 'pt-4 border-t' : ''}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white">v{item.version}</span>
                  <span className="text-xs text-gray-400 dark:text-slate-500">{item.pub_date}</span>
                </div>
                <div className="text-sm text-gray-600 dark:text-slate-300 whitespace-pre-line">{item.notes}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Setup Instructions */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Setup Instructions</h2>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center font-semibold">
              1
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Download and install</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Download the appropriate version for your platform and run the installer.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center font-semibold">
              2
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Register a camera</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Go to the Cameras page and click "Add Camera" to get an API key.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center font-semibold">
              3
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Configure the client</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Enter the API key in the client application to connect it to your dashboard.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Requirements */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">System Requirements</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Windows</h3>
            <ul className="text-sm text-gray-500 dark:text-slate-400 space-y-1">
              <li>• Windows 10 or later (64-bit)</li>
              <li>• 4GB RAM minimum</li>
              <li>• USB or built-in webcam</li>
              <li>• Internet connection</li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Linux</h3>
            <ul className="text-sm text-gray-500 dark:text-slate-400 space-y-1">
              <li>• Ubuntu 20.04+ or equivalent</li>
              <li>• 4GB RAM minimum</li>
              <li>• USB or built-in webcam</li>
              <li>• Internet connection</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );

  // If not authenticated, wrap in public layout with sidebar
  // Otherwise the parent router wraps in Layout
  if (!isAuthenticated) {
    return (
      <PublicLayout>
        {content}
      </PublicLayout>
    );
  }

  // When authenticated, just return content (Layout is provided by router)
  return content;
}
